import React, { useState, useEffect, useCallback } from "react";
import { IAlert } from "../../../../interfaces/alert";
import { ERROR } from "../../../../utils/constants";

interface IPaginationState {
  page: number;
  count: number;
}

interface IUsePaginatedDataParams<T> {
  fetchFn: (
    page: number,
    search?: string,
  ) => Promise<{ data: T[]; count: number }>;
}

interface IUsePaginatedDataReturn<T> {
  data: T[];
  loading: boolean;
  pagination: IPaginationState;
  alert: IAlert;
  searchQuery: string;
  handlePageChange: (newPage: number) => void;
  handleSearch: (query: string) => void;
  handleClearSearch: () => void;
  refresh: () => void;
  setData: React.Dispatch<React.SetStateAction<T[]>>;
  setAlert: React.Dispatch<React.SetStateAction<IAlert>>;
}

/**
 * Hook for managing paginated data with search functionality
 * Centralizes loading state, pagination, and error handling
 */
export const usePaginatedData = <T>({
  fetchFn,
}: IUsePaginatedDataParams<T>): IUsePaginatedDataReturn<T> => {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<IPaginationState>({
    page: 0,
    count: 0,
  });
  const [alert, setAlert] = useState<IAlert>({} as IAlert);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = useCallback(
    async (page: number, search?: string) => {
      setLoading(true);
      try {
        const result = await fetchFn(page, search);
        setData(result.data);
        setPagination({ page, count: result.count });
        setAlert({} as IAlert);
      } catch (error) {
        setAlert({
          typeOfAlert: ERROR,
          message: error instanceof Error ? error.message : String(error),
          showAlert: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [fetchFn],
  );

  useEffect(() => {
    fetchData(0);
  }, []);

  const handlePageChange = useCallback(
    (newPage: number) => {
      fetchData(newPage, searchQuery || undefined);
    },
    [fetchData, searchQuery],
  );

  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      fetchData(0, query || undefined);
    },
    [fetchData],
  );

  const handleClearSearch = useCallback(() => {
    setSearchQuery("");
    fetchData(0);
  }, [fetchData]);

  const refresh = useCallback(() => {
    fetchData(pagination.page, searchQuery || undefined);
  }, [fetchData, pagination.page, searchQuery]);

  return {
    data,
    loading,
    pagination,
    alert,
    searchQuery,
    handlePageChange,
    handleSearch,
    handleClearSearch,
    refresh,
    setData,
    setAlert,
  };
};
